import { useQueryClient } from '@tanstack/react-query';
import { CalendarCheck2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Field, inputCls } from '../../components/ui';
import { api } from '../../lib/api';
import { ApiError } from '../../lib/errors';
import { homeOf, useRole } from '../../lib/role';

/** Вход по паролю (FR-001, FR-003). */
export function Login() {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { role } = useRole();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(login.trim(), password);
      qc.clear();
      navigate(homeOf(role));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LOGIN_LOCKED') {
        const min = Math.max(1, Math.ceil((err.retryAfterSec ?? 900) / 60));
        setError(`Слишком много попыток. Попробуйте через ${min} мин.`);
      } else if (err instanceof ApiError && err.code === 'BAD_CREDENTIALS') {
        setError('Неверный логин или пароль');
      } else {
        setError(
          err instanceof ApiError
            ? `${err.message}${err.requestId ? ` (код ошибки: ${err.requestId})` : ''}`
            : 'Ошибка входа',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-hero-a via-canvas-2 to-hero-b px-4">
      <div className="w-full max-w-[400px]">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-control bg-accent text-white">
            <CalendarCheck2 size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <span className="text-[20px] font-semibold tracking-[-0.01em]">Табель</span>
        </div>
        <form
          onSubmit={submit}
          className="rounded-sheet border border-line bg-surface p-7 shadow-pop max-sm:p-5"
        >
          <h1 className="text-[18px] font-semibold">Вход</h1>
          <p className="mt-1 mb-6 text-[13px] text-ink-2">Табель учёта рабочего времени по данным СКУД</p>
          <Field label="Логин">
            <input
              className={inputCls}
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>
          <Field label="Пароль">
            <input
              className={inputCls}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>
          {error && (
            <p role="alert" className="mb-4 rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" size="lg" className="mt-1 w-full" disabled={busy}>
            {busy ? 'Вход…' : 'Войти'}
          </Button>
        </form>
        <p className="mt-5 text-center text-[12px] text-ink-3">Учётную запись выдаёт администратор системы</p>
      </div>
    </div>
  );
}
